import React, { Fragment, useContext, useEffect } from 'react';
import {
  AAMBeaconButton,
  AAMBeaconControlGroup,
  AAMBeaconPopover,
  AAMBeaconIconButton,
  AAMBeaconControl
} from 'components/shared/Beacon';
import {
  aamToBeaconFormatter,
  beaconToAamFormatter,
  isBeneficiaryPrimary
} from 'services/Beneficiary';
import {
  ENTITY_ID,
  INDIVIDUAL_ID,
  PRIMARY_ID,
  SECONDARY_ID,
  findBeneficiaryById,
  handleBeneFormInputChange,
  popoverContent,
  onRelationshipChange,
  beneficiarySuffixOptionsV3,
  formatEINtoBeaconField,
  formatSSNtoBeaconField,
  beneficiaryAddDisclaimer,
  formatStringIfEducationSavings,
  replaceStringIfEducationSavings,
  beneficiaryAddDisclaimerTOD,
  capitalizeName,
  beneficiaryRelationshipOptionsV2,
  renderRelationshipDropdownHeader,
  isLastPrimaryBeneficiary
} from 'services/BeneficiaryV2';
import BeneficiaryContext from '../BeneficiaryContext';
import { ReactComponent as Trashcan } from 'assets/icons/beacon/Trashcan.svg';
import moment from 'moment';
import FormLabelWithPopover from 'components/shared/FormLabelWithPopover/FormLabelWithPopover';
import { cloneDeep } from "lodash";
import Store from 'state/Store';
import { beneSteps } from '../Utils';
import { PageContext } from '../StepProvider';
import { updateBeneficiary } from 'state/beneficiaries/BeneficiaryActions';
import { ReactComponent as Icon } from 'assets/icons/beacon/Information.svg';
import { BENE_ENTITY_NAME_REGEX, BENE_NAME_REGEX, handleInput } from 'services/Validation';
import { isTransferOnDeath } from 'services/AccountTypes';
import './BeneficiaryForm.scss';

const BeneficiaryEditForm = () => {
  const {
    selectedBeneficiaryId,
    updatedBeneficiary,
    setUpdatedBeneficiary,
    setDeletedBeneficiary,
    beneficiaries,
    setInvalidField,
    setIsinvalidDelete,
    isEsa,
    agreementsForBeneUpdate
  } = useContext(BeneficiaryContext);
  const { goToPage } = useContext(PageContext);


  useEffect(() => {
    const beneficiary = findBeneficiaryById(selectedBeneficiaryId, beneficiaries);
    if (beneficiary) {
      const name = beneficiary.typeId === INDIVIDUAL_ID
        ? [beneficiary.firstName, beneficiary.middleName, beneficiary.lastName, beneficiary.nameSuffix].filter(Boolean).join(' ')
        : beneficiary.name;

      setUpdatedBeneficiary({
        designationId: beneficiary.designationId,
        typeId: beneficiary.typeId,
        firstName: beneficiary.firstName,
        middleName: beneficiary.middleName,
        lastName: beneficiary.lastName,
        nameSuffix: beneficiary.nameSuffix,
        birthDate: beaconToAamFormatter(beneficiary.birthDate),
        taxId: beneficiary.taxId,
        relationshipName: beneficiary.typeId === ENTITY_ID ? 'Entity' : beneficiary.relationshipName,
        relationshipId: beneficiary.relationshipId,
        name: name,
        businessEntityName: beneficiary.businessEntityName,
        _id: beneficiary._id
      });
    }
  }, [selectedBeneficiaryId, beneficiaries]);

  const handleInputChange = (field, value) => handleBeneFormInputChange(field, value, setUpdatedBeneficiary, setInvalidField([]));

  const handleDeleteClick = () => {
    if (!beneficiaries) {
      return;
    }

    const beneToDelete = cloneDeep(updatedBeneficiary);
    if (isBeneficiaryPrimary(beneToDelete) && beneficiaries.primary.length === 1 && (beneficiaries.secondary.length > 0 || isTransferOnDeath(agreementsForBeneUpdate[0].accountTypeId))) {
      setIsinvalidDelete(true);
      goToPage(beneSteps.BENE_MANAGE_STEP);
    } else {
      beneToDelete.deleted = true;
      setDeletedBeneficiary(beneToDelete);
      setIsinvalidDelete(false);
      Store.dispatch(updateBeneficiary(selectedBeneficiaryId, beneToDelete));
      goToPage(beneSteps.BENE_MANAGE_STEP);
    }
  };

  const shouldDisplayRadioButtons = !isEsa;
  const designationRadioButtonsShouldBeDisabled = isLastPrimaryBeneficiary(beneficiaries.primary, selectedBeneficiaryId);

  return (
    <>
      {shouldDisplayRadioButtons && (
        <>
          <div className="b-grid-item--12-col b-md:10-col">
            <AAMBeaconControlGroup disabled={designationRadioButtonsShouldBeDisabled} data-testid="designationId">
              <div slot='label' className='beacon-on-dark title-with-icon' label="designationId" >
                <span className="b-typography-sans-serif--headline--50 b-font-weight-bold">
                  Would you like to name a primary or secondary beneficiary?</span>
                <AAMBeaconIconButton id="popover-bene-trigger" variant='tertiary' aria-label="beneficiary-definition">
                  <Icon slot="icon" />
                </AAMBeaconIconButton>
                <AAMBeaconPopover
                  opens-by="#popover-bene-trigger"
                  placement='left-end'
                >
                  <h2 className={'b-font-weight-bold'}>{popoverContent.BENEFICIARY_DEFINITION.title}</h2>
                  <div>{popoverContent.BENEFICIARY_DEFINITION.answer}</div>
                </AAMBeaconPopover>
              </div>
              <AAMBeaconControl inline>
                <label slot="label" htmlFor={PRIMARY_ID}>Primary beneficiary</label>
                <input
                  type="radio"
                  aria-label={PRIMARY_ID}
                  checked={updatedBeneficiary.designationId === PRIMARY_ID}
                  id='PRIMARY_ID'
                  name="designationId"
                  onChange={() => handleInputChange('designationId', PRIMARY_ID)} />
              </AAMBeaconControl>
              <AAMBeaconControl inline>
                <label slot="label" htmlFor={SECONDARY_ID}>Secondary beneficiary</label>
                <input
                  type="radio"
                  aria-label={SECONDARY_ID}
                  checked={updatedBeneficiary.designationId === SECONDARY_ID}
                  id='SECONDARY_ID'
                  name="designationId"
                  onChange={() => handleInputChange('designationId', SECONDARY_ID)} />
              </AAMBeaconControl>
            </AAMBeaconControlGroup>
          </div>
          <div className="b-grid-item--12-col">
            <AAMBeaconControlGroup data-testid="typeId" >
              <div slot="label"><span className="b-typography-sans-serif--headline--50 b-font-weight-bold">Beneficiary type</span></div>
              <AAMBeaconControl inline>
                <label slot="label" htmlFor={INDIVIDUAL_ID}>Individual</label>
                <input
                  id={INDIVIDUAL_ID}
                  type="radio"
                  checked={updatedBeneficiary.typeId === INDIVIDUAL_ID}
                  name="typeId"
                  onChange={() => handleInputChange('typeId', INDIVIDUAL_ID)} />
              </AAMBeaconControl>
              <AAMBeaconControl inline>
                <label slot="label" htmlFor={ENTITY_ID}>Entity</label>
                <input
                  type="radio"
                  checked={updatedBeneficiary.typeId === ENTITY_ID}
                  id={ENTITY_ID}
                  name="typeId"
                  onChange={() => handleInputChange('typeId', ENTITY_ID)} />
              </AAMBeaconControl>
            </AAMBeaconControlGroup>
          </div>
          <div className="b-grid-item--12-col">
            {agreementsForBeneUpdate && agreementsForBeneUpdate.length > 0
              && isTransferOnDeath(agreementsForBeneUpdate[0].accountTypeId) ? beneficiaryAddDisclaimerTOD : beneficiaryAddDisclaimer}
          </div>
        </>
      )}
      {updatedBeneficiary.typeId === INDIVIDUAL_ID && (
        <Fragment>
          <div className="b-grid-item--12-col blocked_data">
            <AAMBeaconControl>
              <label htmlFor="first-name" slot="label">
                First name
              </label>
              <input
                type="text"
                name="firstName"
                id="first-name"
                value={updatedBeneficiary.firstName}
                required={true}
                pattern={BENE_NAME_REGEX.source}
                onChange={(e) =>
                  handleInputChange('firstName', e.target.value)
                }
                minLength="1"
                maxLength="35"
                data-testid="firstName"
              />
            </AAMBeaconControl>
          </div>
          <div className="b-grid-item--12-col blocked_data">
            <AAMBeaconControl>
              <label htmlFor="middle-name" slot="label">
                Middle name (optional)
              </label>
              <input
                type="text"
                name="middleName"
                id="middle-name"
                data-testid="middleName"
                pattern={BENE_NAME_REGEX.source}
                value={updatedBeneficiary.middleName}
                minLength="1"
                maxLength="35"
                onChange={(e) =>
                  handleInputChange('middleName', e.target.value)
                }
              />
            </AAMBeaconControl>
          </div>
          <div className="b-grid-item--12-col blocked_data">
            <AAMBeaconControl>
              <label htmlFor="last-name" slot="label">
                Last name
              </label>
              <input
                type="text"
                name="lastName"
                id="last-name"
                value={updatedBeneficiary.lastName}
                required={true}
                pattern={BENE_NAME_REGEX.source}
                onChange={(e) =>
                  handleInputChange('lastName', e.target.value)
                }
                minLength="1"
                maxLength="35"
                data-testid="lastName"
              />
            </AAMBeaconControl>
          </div>
          <div className="b-grid-item--6-col b-sm:12-col b-md:6-col blocked_data">
            <AAMBeaconControl>
              <label slot="label" htmlFor="nameSuffix">Suffix (optional)</label>
              <select
                onChange={(e) => handleInputChange('nameSuffix', e.target.value)}
                value={capitalizeName(updatedBeneficiary.nameSuffix)}
                id="nameSuffix">
                <option key="default" value="" defaultValue={''}>
                  Select
                </option>
                {beneficiarySuffixOptionsV3.map((option) => (
                  <option value={option.value} key={`${option.value}-dSuffix`}>
                    {option.label}
                  </option>
                ))}
              </select>
            </AAMBeaconControl>
          </div>
          <div className="b-grid-item--6-col b-sm:12-col b-md:6-col blocked_data">
            <AAMBeaconControl>
              <label slot="label" htmlFor="relationshipName">{renderRelationshipDropdownHeader(isEsa)}</label>
              <select
                onChange={onRelationshipChange(handleInputChange)}
                value={updatedBeneficiary.relationshipId === 0 ? "" : updatedBeneficiary.relationshipId.toString()}
                id="relationshipName"
                required
              >
                <option key="default" value="" disabled defaultValue={''}>
                  Select
                </option>
                {beneficiaryRelationshipOptionsV2.map((option) => (
                  <option value={option.value.toString()} key={`${option.value}-dRelation`}
                    selected={option.label === updatedBeneficiary.relationshipName}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              <div slot="error">Relationship is required</div>
            </AAMBeaconControl>
          </div>
          <div className="b-grid-item--6-col b-sm:12-col b-xl:6-col blocked_data">
            <AAMBeaconControl >
              <FormLabelWithPopover
                className={'date-of-birth'}
                title="Date of birth (optional)"
                content={formatStringIfEducationSavings(popoverContent.DATE_BIRTH.answer, isEsa)}
                popoverTitle={popoverContent.DATE_BIRTH.title}
              />
              <input
                type="date"
                aria-label="date-picker"
                max={moment().format('YYYY-MM-DD')}
                id="date-picker"
                required={false}
                data-testid="birthDate"
                name="birthDatePicker"
                value={aamToBeaconFormatter(updatedBeneficiary.birthDate)}
                onChange={(event) =>
                  handleInputChange(
                    'birthDate',
                    beaconToAamFormatter(event.target.value)
                  )} />
            </AAMBeaconControl>
          </div>
          <div className="b-grid-item--6-col b-sm:12-col b-xl:6-col blocked_data">
            <AAMBeaconControl>
              <FormLabelWithPopover
                className={'ssn'}
                placement='top-end'
                title="Social Security/TIN (optional)"
                content={formatStringIfEducationSavings(popoverContent.SOCIAL_SECURITY.answer, isEsa)}
                popoverTitle={popoverContent.SOCIAL_SECURITY.title}
              />
              <input
                type="text"
                aria-label='taxId'
                name="taxIdField"
                id="taxId"
                value={formatSSNtoBeaconField(updatedBeneficiary.taxId)}
                onChange={(e) =>
                  handleInputChange('taxId', e.target.value)
                }
                required={false}
                minLength="11"
                maxLength="11"
                data-testid="taxId"
                onInput={(e) => handleInput(e)}
              />
            </AAMBeaconControl>
          </div>
        </Fragment>
      )}
      {updatedBeneficiary.typeId === ENTITY_ID && (
        <Fragment>
          <div className="b-grid-item--12-col blocked_data">
            <AAMBeaconControl>
              <label htmlFor="businessEntityName" slot="label">
                Name
              </label>
              <input
                type="text"
                name="businessEntityName"
                id="businessEntityName"
                data-testid="entityName"
                required={true}
                minLength="1"
                maxLength="70"
                pattern={BENE_ENTITY_NAME_REGEX.source}
                value={updatedBeneficiary.businessEntityName}
                onChange={(e) => handleInputChange('businessEntityName', e.target.value)}
              />
            </AAMBeaconControl>
          </div>
          <div className="b-grid-item--6-col b-sm:12-col b-xl:6-col blocked_data">
            <AAMBeaconControl>
              <FormLabelWithPopover
                className={'ssn'}
                title="Social Security/TIN (optional)"
                content={popoverContent.SOCIAL_SECURITY.answer}
                popoverTitle={popoverContent.SOCIAL_SECURITY.title}
              />
              <input
                type="text"
                name="taxId"
                id="tin"
                data-testid="taxId"
                minLength="10"
                maxLength="10"
                value={formatEINtoBeaconField(updatedBeneficiary.taxId)}
                onChange={(e) =>
                  handleInputChange('taxId', e.target.value)
                }
                onInput={(e) => handleInput(e)}
              />
            </AAMBeaconControl>
          </div>
        </Fragment>
      )}
      <div className="b-grid-item--12-col">
        <AAMBeaconButton
          variant="negative"
          onBeaconEventClick={handleDeleteClick}
        ><Trashcan slot={'prefix'} />{replaceStringIfEducationSavings('Delete Beneficiary', isEsa)}</AAMBeaconButton>
      </div>
    </>
  );
};

export default BeneficiaryEditForm;

